import { createFeatureSelector, createSelector } from '@ngrx/store';
import { MarginCalculatorState } from '../state/margin-calculator.state';
import { marginCalculatorFeatureKey } from './margin-calculator.feature';
import { DuplicateMarginActions, PipelineMarginActions, ReviewAndSendActions, UpdateMarginActions } from '../actions';
import { ApiActionStatus } from '../../enum';

const selectMarginCalculatorState = createFeatureSelector<MarginCalculatorState>(marginCalculatorFeatureKey);

export const selectSaveMarginAction = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.saveMarginAction
);

/**
 * Select the type of the save margin action. Possible values are defined by
 * full action name.
 *
 * Save, Duplicate, Pipeline, Review and Send, Create Contract Request. Specific
 * types are defined as separate selectors.
 */
export const selectSaveMarginActionType = createSelector(
    selectSaveMarginAction,
    (marginAction) => marginAction?.actionType
);

/** Selects the flag indicating whether current save margin action has "Save Margin" type */
export const selectSaveMarginActionTypeSaveMargin = createSelector(
    selectSaveMarginActionType,
    (actionType: string) => !!actionType && actionType === UpdateMarginActions.saveMargin.type
);

/**
 * Selects the flag indicating whether current save margin action has "Review and Send" type
 */
export const selectSaveMarginActionTypeReviewAndSend = createSelector(
    selectSaveMarginActionType,
    (actionType: string) => !!actionType && actionType === ReviewAndSendActions.reviewAndSendMargin.type
);

/**
 * Selects the flag indicating whether current save margin action has "Create Contract Request" type
 */
export const selectSaveMarginActionTypeCreateContractRequest = createSelector(
    selectSaveMarginActionType,
    (actionType: string) => !!actionType && actionType === ReviewAndSendActions.createContractRequest.type
);

/**
 * Selects the flag indicating whether current save margin action has "Pipeline" type
 */
export const selectSaveMarginActionTypePipeline = createSelector(
    selectSaveMarginActionType,
    (actionType: string) => !!actionType && actionType === PipelineMarginActions.pipelineMargin.type
);

/**
 * Selects the flag indicating whether current save margin action has "Duplicate" type
 */
export const selectSaveMarginActionTypeDuplicate = createSelector(
    selectSaveMarginActionType,
    (actionType: string) => !!actionType && actionType === DuplicateMarginActions.duplicateMargin.type
);

/** Selects the status of the current "Save Margin" action */
export const selectSaveMarginActionStatus = createSelector(
    selectSaveMarginAction,
    (marginAction) => marginAction?.status
);

/**
 * Select the flag, indicating whether the status of the current "save margin" action is "success"
 */
export const selectSaveMarginActionStatusSuccess = createSelector(
    selectSaveMarginActionStatus,
    (status) => !!status && status === ApiActionStatus.Success
);

/**
 * Select the flag, indicating whether the status of the current "save margin" action is "failed"
 */
export const selectSaveMarginActionStatusFailed = createSelector(
    selectSaveMarginActionStatus,
    (status) => !!status && status === ApiActionStatus.Failed
);

/**
 * Select the flag, indicating whether the status of the current "save margin" action is "in progress"
 */
export const selectSaveMarginActionStatusInProgress = createSelector(
    selectSaveMarginActionStatus,
    (status) => !!status && status === ApiActionStatus.InProgress
);

export const selectMarginValidationRules = createSelector(
    selectMarginCalculatorState,
    (state: MarginCalculatorState) => state.marginValidationRules
);
